import React, { useCallback, useEffect, useRef, useState } from "react";
import { assignWork } from "../../../api/hub/work";
import { assignThread, getAssignableMembers } from "../../../api/conversations";
import { getMyUserId } from "../../../api/hub/now";
import AssignmentPopover from "../work/overlays/AssignmentPopover";
import { popupBg, popupBorder, popupText, popupSubtext, popupBtn } from "../work/constants";
import { isAssignable, canAssign } from "./kindMeta";
import { BTN_ASSIGN, BTN_PRIMARY } from "./buttonStyles";

/**
 * The refusal message when an RPC answered with the gate envelope `{error: true, message}`
 * instead of throwing — either bare (the thread path) or as the payload inside the work
 * wrapper's own success envelope (the task path). Null when the call really succeeded.
 * @param {unknown} result
 * @returns {string|null}
 */
const gateRefusal = (result) => {
  if (result?.error === true) return result.message || "The assignment was refused.";
  if (result?.data?.error === true) {
    return result.data.message || "The assignment was refused.";
  }
  return null;
};

/**
 * Assign one Now item to a team member, via the write that matches its subject:
 * `task` → fn_work_assign, `thread` → fn_thread_assign. Any other subject has no assign
 * write and is skipped.
 *
 * The return value exists as a TEST SEAM — callers ignore it. It makes the guard and the
 * catch each observable: deleting the subject guard flips a `"skipped"` case, and the
 * catch is the only path that returns `"failed"`. Do not delete it as unused.
 *
 * On failure the RPC's message is handed to `onError` verbatim (e.g. fn_thread_assign
 * refusing a DM), and `onAssigned` is not called, so the row stays as it was. A gate
 * refusal that comes back as a value rather than a throw counts as a failure too.
 *
 * @param {{id: string, subject_type?: string}} item
 * @param {{id: string, name?: string}} member  The new owner.
 * @param {{onAssigned?: (item: object, patch: {owner: string, owner_id: string}) => void,
 *          onError?: (message: string) => void}} [callbacks]
 * @returns {Promise<"assigned"|"skipped"|"failed">}
 */
export async function assignItem(item, member, { onAssigned, onError } = {}) {
  if (!isAssignable(item) || !member?.id) return "skipped";
  try {
    const result =
      item.subject_type === "task"
        ? await assignWork(item.id, member.id)
        : await assignThread(item.id, member.id);
    const refusal = gateRefusal(result);
    if (refusal) {
      onError?.(refusal);
      return "failed";
    }
  } catch (e) {
    onError?.(e?.message || "Couldn't assign this item.");
    return "failed";
  }
  onAssigned?.(item, { owner: member.name || "Assigned", owner_id: member.id });
  return "assigned";
}

// "Assign to me" is a convenience: failing to learn who I am must never break the menu.
const fetchMyId = async () => {
  try {
    return (await getMyUserId()) ?? null;
  } catch {
    return null;
  }
};

/**
 * Assign one unowned Now item to the signed-in user ("Take"), through the same write as
 * Assign. The user is identified only when their id is one of the assignable members, so
 * this never guesses: an unresolved identity is reported through `onError` and nothing is
 * written.
 *
 * The return value exists as a TEST SEAM — callers ignore it. `"skipped"` is the guard for
 * a row that cannot be assigned or is already owned, and `"failed"` covers every path that
 * writes nothing or was refused. Do not delete it as unused.
 *
 * @param {object} item  One fn_now item.
 * @param {{onAssigned?: Function, onError?: (message: string) => void}} [callbacks]
 * @returns {Promise<"assigned"|"skipped"|"failed">}
 */
export async function takeItem(item, { onAssigned, onError } = {}) {
  if (!canAssign(item)) return "skipped";
  let me = null;
  try {
    const [rows, uid] = await Promise.all([getAssignableMembers(), fetchMyId()]);
    me = uid && Array.isArray(rows) ? rows.find((m) => m.id === uid) || null : null;
  } catch (e) {
    onError?.(e?.message || "Couldn't load the team.");
    return "failed";
  }
  if (!me) {
    onError?.("Couldn't tell which team member you are, so this can't be taken. Use Assign instead.");
    return "failed";
  }
  return assignItem(item, me, { onAssigned, onError });
}

/**
 * The primary "Take" button on an unowned task row. Renders nothing for an owned row or a
 * subject with no assign write, never a disabled button.
 * @param {object} props
 * @param {object} props.item
 * @param {Function} [props.onAssigned]
 * @param {(message: string) => void} [props.onError]
 */
export function NowTakeButton({ item, onAssigned, onError }) {
  const [busy, setBusy] = useState(false);
  if (!canAssign(item)) return null;
  const take = async () => {
    setBusy(true);
    await takeItem(item, { onAssigned, onError });
    setBusy(false);
  };
  return (
    <button
      type="button"
      data-action="take"
      disabled={busy}
      onClick={take}
      className={`${BTN_PRIMARY} disabled:opacity-50`}
    >
      Take
    </button>
  );
}

/**
 * The dashed "Assign" control on an unowned Now row. Renders nothing for an owned row or
 * a subject with no assign write (commitment, message, invoice, …) — never a disabled
 * button.
 *
 * "Assign to me" leads the menu, and only appears when the signed-in user is one of the
 * assignable members (so it never guesses an id). Task rows then open the shared
 * AssignmentPopover for everyone else; thread rows list the members inline.
 *
 * @param {object} props
 * @param {object} props.item  One fn_now item.
 * @param {(item: object, patch: {owner: string, owner_id: string}) => void} [props.onAssigned]
 * @param {(message: string) => void} [props.onError]  Receives the RPC message verbatim.
 */
export default function NowAssignControl({ item, onAssigned, onError }) {
  const [open, setOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [members, setMembers] = useState(null); // null until loaded
  const [meId, setMeId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [rows, uid] = await Promise.all([
        getAssignableMembers(),
        fetchMyId(),
      ]);
      setMembers(Array.isArray(rows) ? rows : []);
      setMeId(uid);
    } catch (e) {
      setLoadError(e?.message || "Couldn't load the team.");
    }
  }, []);

  // Close on outside click. The popover is a full-screen overlay with its own dismissal,
  // so this only runs for the inline menu.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!canAssign(item)) return null;

  const isTask = item.subject_type === "task";
  const me = members?.find((m) => m.id === meId) || null;
  const others = (members || []).filter((m) => m.id !== meId);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && members === null) load();
  };

  const pick = async (member) => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    await assignItem(item, member, { onAssigned, onError });
    setBusy(false);
  };

  // AssignmentPopover reports only the chosen id; resolve the name from the roster so
  // the row can show the new owner. A failure is reported through onError (the popover
  // itself would only console.error it) and never rethrown, so the popover just closes.
  const pickById = (id) => pick((members || []).find((m) => m.id === id) || { id });

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        data-action="assign"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={toggle}
        className={`${BTN_ASSIGN} disabled:opacity-50`}
      >
        Assign
      </button>

      {open && (
        <div
          role="menu"
          data-testid="now-assign-menu"
          className={`absolute right-0 top-full z-20 mt-1 w-56 rounded-lg p-1 shadow-lg ${popupBg} ${popupBorder} ${popupText}`}
        >
          {members === null && !loadError && (
            <p className={`px-2 py-2 text-sm ${popupSubtext}`}>Loading team…</p>
          )}
          {loadError && (
            <div className="px-2 py-2 text-sm text-red-600 dark:text-red-400" data-testid="now-assign-load-error">
              <p>{loadError}</p>
              <button type="button" onClick={load} className="mt-1 underline">
                Try again
              </button>
            </div>
          )}
          {me && (
            <button
              type="button"
              role="menuitem"
              data-testid="now-assign-me"
              onClick={() => pick(me)}
              className={`block w-full rounded-md px-2 py-1.5 text-left text-sm font-medium ${popupBtn}`}
            >
              Assign to me
            </button>
          )}
          {members !== null && isTask && (
            <button
              type="button"
              role="menuitem"
              data-testid="now-assign-choose"
              onClick={() => {
                setOpen(false);
                setPopoverOpen(true);
              }}
              className={`block w-full rounded-md px-2 py-1.5 text-left text-sm ${popupBtn}`}
            >
              Choose someone…
            </button>
          )}
          {members !== null && !isTask && (
            <ul className="max-h-56 overflow-y-auto">
              {others.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    role="menuitem"
                    data-member={m.id}
                    onClick={() => pick(m)}
                    className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-sm ${popupBtn}`}
                  >
                    {m.name}
                  </button>
                </li>
              ))}
              {others.length === 0 && !me && (
                <li className={`px-2 py-2 text-sm ${popupSubtext}`}>No team members.</li>
              )}
            </ul>
          )}
        </div>
      )}

      {popoverOpen && (
        <AssignmentPopover
          taskId={item.id}
          taskTitle={item.title}
          workType={item.work_type}
          clientId={item.client_id}
          teamMembers={members || []}
          onSelect={pickById}
          onClose={() => setPopoverOpen(false)}
        />
      )}
    </div>
  );
}
